import { createSlice } from "@reduxjs/toolkit";
import {
  createMonthLock,
  fetchAuditLogs,
  fetchMonthLocks,
  unlockMonthLock,
} from "../Thunks/reportsThunk";
import { isSupersededRequestPayload } from "../helpers/asyncResource";
import { monthLockRowFromCreateResponse, monthLockRowFromListItem } from "../../utils/monthLockApi";

const auditLogsInitial = {
  items: [],
  total: 0,
  page: 1,
  limit: 20,
  loading: false,
  error: null,
};

const initialState = {
  /** Month lock rows — GET /month-locks + POST /month-locks (+ local unlock optimistics). */
  data: {
    monthLocks: [],
    monthLocksLoading: false,
    monthLocksError: null,
  },
  auditLogs: auditLogsInitial,
};

export const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    clearReports: (state) => {
      state.data = {
        monthLocks: [],
        monthLocksLoading: false,
        monthLocksError: null,
      };
      state.auditLogs = { ...auditLogsInitial };
    },
    lockMonthLocally: (state, action) => {
      const { monthId, lockedBy, lockedOn } = action.payload;
      if (!state.data?.monthLocks) return;

      const month = state.data.monthLocks.find((m) => m.id === monthId);
      if (!month) return;

      const previousStatus = month.status;
      month.status = "Locked";
      month.lockedBy = lockedBy;
      month.lockedOn = lockedOn;

      state.auditLogs.items.unshift({
        id: `local-${Date.now()}`,
        timestamp: new Date().toISOString(),
        user: lockedBy,
        action: "LOCK",
        month: `${month.month} ${month.year}`,
        previousStatus,
        newStatus: "Locked",
        reason: null,
      });
    },
    unlockMonthLocally: (state, action) => {
      const { monthId, unlockedBy, reason, unlockedOn } = action.payload;
      if (!state.data?.monthLocks) return;

      const month = state.data.monthLocks.find((m) => m.id === monthId);
      if (!month) return;

      const previousStatus = month.status;

      month.status = "Unlocked";
      month.lockedBy = unlockedBy || "—";
      month.lockedOn = unlockedOn || "—";

      state.auditLogs.items.unshift({
        id: `local-${Date.now()}`,
        timestamp: new Date().toISOString(),
        user: unlockedBy,
        action: "UNLOCK",
        month: `${month.month} ${month.year}`,
        previousStatus,
        newStatus: "Unlocked",
        reason: reason != null && String(reason).trim() !== "" ? String(reason) : null,
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createMonthLock.fulfilled, (state, action) => {
        const raw = action.payload.body;
        const created = raw?.data?.data ?? raw?.data ?? raw;
        const meta = action.payload.meta;
        const row = monthLockRowFromCreateResponse(created, meta);
        if (!row) return;
        const idx = state.data.monthLocks.findIndex(
          (m) => `${m.month}|${m.year}` === `${row.month}|${row.year}` && m.client === row.client
        );
        if (idx >= 0) state.data.monthLocks[idx] = row;
        else state.data.monthLocks.push(row);
      })
      .addCase(unlockMonthLock.fulfilled, (state, action) => {
        const lockId = action.meta.arg.lockId;
        const raw = action.payload.body;
        const updated = raw?.data?.data ?? raw?.data ?? raw;
        const row = monthLockRowFromListItem(updated);
        const idx = state.data.monthLocks.findIndex((m) => String(m.id) === String(lockId));
        if (idx < 0) return;
        if (row) {
          state.data.monthLocks[idx] = row;
        } else {
          const m = state.data.monthLocks[idx];
          m.status = "Unlocked";
          m.lockedBy = m.lockedBy || "—";
          m.lockedOn = m.lockedOn || "—";
        }
      })
      .addCase(fetchMonthLocks.pending, (state) => {
        state.data.monthLocksLoading = true;
        state.data.monthLocksError = null;
      })
      .addCase(fetchMonthLocks.fulfilled, (state, action) => {
        state.data.monthLocksLoading = false;
        state.data.monthLocksError = null;
        state.data.monthLocks = action.payload.items;
      })
      .addCase(fetchMonthLocks.rejected, (state, action) => {
        if (isSupersededRequestPayload(action.payload)) return;
        state.data.monthLocksLoading = false;
        state.data.monthLocksError =
          action.payload ?? action.error?.message ?? "Failed to fetch month locks";
      })
      .addCase(fetchAuditLogs.pending, (state) => {
        state.auditLogs.loading = true;
        state.auditLogs.error = null;
      })
      .addCase(fetchAuditLogs.fulfilled, (state, action) => {
        state.auditLogs.loading = false;
        state.auditLogs.error = null;
        state.auditLogs.items = action.payload.items;
        state.auditLogs.total = action.payload.total;
        state.auditLogs.page = action.payload.page;
        state.auditLogs.limit = action.payload.limit;
      })
      .addCase(fetchAuditLogs.rejected, (state, action) => {
        if (isSupersededRequestPayload(action.payload)) return;
        state.auditLogs.loading = false;
        state.auditLogs.error =
          action.payload ?? action.error?.message ?? "Failed to fetch audit logs";
      });
  },
});

export const { clearReports, lockMonthLocally, unlockMonthLocally } = reportsSlice.actions;

export const selectReportsData = (state) => state.reports.data;
export const selectAuditLogsItems = (state) => state.reports.auditLogs.items;
export const selectAuditLogsLoading = (state) => state.reports.auditLogs.loading;
export const selectAuditLogsError = (state) => state.reports.auditLogs.error;

export default reportsSlice.reducer;
